<template>
  <div
    ref="mainRef"
    class="ui-pivot-table"
  >
    <div class="ui-pivot-table__container">
      <Sidebar
        v-show="!readonly"
        v-if="config?.open"
        v-model="config"
      >
        <slot name="sidebar" />
      </Sidebar>
      <div class="ui-pivot-table__content">
        <Panel
          v-if="!readonly"
          v-model="config"
          :sort-columns-fields
          :sort-rows-fields
          @export="handleClickExport"
          @expand="handleClickExpand"
        >
          <slot
            name="filter"
            size="xxs"
          />
        </Panel>
        <Content
          v-if="data"
          ref="contentRef"
          :cols-structure
          :data
          :enabled-fields
          :rows-list
          :sort-rows
          :values-list
          :layout="config?.layout"
          @click:value="handleClickValue"
          @click:text="handleClickText"
        >
          <template #total="slotProps">
            <slot
              name="total"
              v-bind="slotProps"
            />
          </template>
          <template #header="slotProps">
            <slot
              name="header"
              v-bind="slotProps"
            />
          </template>
        </Content>
        <UILayoutNoData
          v-else-if="!isLoading"
          inline
          hide-question-icon
          class="ui-pivot-table__container__no-data"
        />
        <slot name="bottom" />
        <UIPreloader
          v-if="isLoading"
          overlay
          message="Prepare pivot configuration..."
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  defineAsyncComponent,
  hydrateOnVisible,
  nextTick,
  provide,
  ref,
  useTemplateRef,
} from 'vue'
import { computedAsync } from '@vueuse/core'
import { cloneDeep, sumBy } from 'lodash'

import {
  PivotTableCaptions,
  PivotTableConfig,
  PivotTableData,
  PivotTableFields,
  PivotTableValueGetter,
  PivotTableValueRenderers,
  PivotTableTooltips,
  PivotTableClickValueData,
} from './utils/types'
import { PivotTableGroup } from './utils/enums'

import { UNKNOWN_VALUE } from '@/const'

import {
  clearColsStructure,
  getEnabledFields,
  handleCols,
  handleValues,
  prepareSortField,
  prepareValueSortField,
} from './utils/helpers'
import { exportCSV } from '@/helpers/common'

import Loader from './components/Loader.vue'
import Panel from './components/Panel.vue'
import Sidebar from './components/Sidebar.vue'
import UILayoutNoData from '../LayoutNoData/LayoutNoData.vue'
import UIPreloader from '../Preloader/Preloader.vue'

export type Props = {
  data?: PivotTableData[]

  fields: PivotTableFields
  captions?: PivotTableCaptions
  tooltips?: PivotTableTooltips
  renderers?: PivotTableValueRenderers
  clickable?: string[]

  valueGetter?: PivotTableValueGetter

  frozenFields?: string[]

  disableCols?: boolean

  loading?: boolean

  sortable?: boolean

  readonly?: boolean
}

type Emits = {
  'click:value': [data: PivotTableClickValueData, amount: number]
  'click:text': [data: string]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const config = defineModel<PivotTableConfig>()

defineExpose({
  async refresh() {
    await nextTick()
    internalConfig.value = cloneDeep(config.value)
  },
})

const Content = defineAsyncComponent({
  loader: () => import('./components/Content.vue'),
  loadingComponent: Loader,
  hydrate: hydrateOnVisible(),
  delay: 0,
})

const internalConfig = ref<PivotTableConfig>()

const contentRef = useTemplateRef('contentRef')

provide('captions', computed(() => props.captions))
provide('tooltips', computed(() => props.tooltips))
provide('renderers', computed(() => props.renderers))
provide('clickable', props.clickable)
provide('valueGetter', props.valueGetter)
provide('disableCols', props.disableCols)

const enabledFields = computedAsync<string[] | undefined>(
  async () => {
    return new Promise(resolve => {
      if (props.loading) return
      resolve(
        internalConfig.value?.fields &&
          getEnabledFields(internalConfig.value.fields),
      )
    })
  },
  undefined,
  { lazy: true },
)

const isLoading = computed(() => props.loading)

const fieldsList = computed(() => internalConfig.value?.fields)

const colsList = computed(() =>
  getEnabledFields(fieldsList.value, [PivotTableGroup.COLS]),
)
provide('colsList', colsList)
const rowsList = computed(() =>
  getEnabledFields(fieldsList.value, [PivotTableGroup.ROWS]),
)
const valuesList = computed(() =>
  getEnabledFields(fieldsList.value, [PivotTableGroup.VALUES]),
)

const valuesCols = computed(() =>
  handleValues(valuesList.value, props.captions),
)

const sortCols = computed(() => config.value?.sortColumns)
const sortRows = computed(() => config.value?.sortRows)

const colsStructure = computed(() => {
  const anyCols = !!Object.values(props.fields || {})?.find(
    field => field.group === PivotTableGroup.COLS,
  )
  return handleCols(
    props.data,
    colsList.value,
    rowsList.value,
    anyCols,
    sortCols.value,
    valuesCols.value,
    props.valueGetter,
    props.renderers,
  )
})

const sortColumnsFields = computed(() => {
  if (!colsList.value) return
  return [...colsList.value, 'footer_grand_total']
    ?.filter(field => !props.frozenFields?.includes(field))
    ?.map(prepareSortField(props.captions))
})

const sortRowsFields = computed(() => {
  const list = rowsList.value
    ?.filter(field => !props.frozenFields?.includes(field))
    ?.map(prepareSortField(props.captions))
  const cols =
    clearColsStructure(colsStructure.value)?.filter(col =>
      Object.keys(col).every(field => !props.frozenFields?.includes(field)),
    ) || []
  return list?.concat(
    cols.map(col => prepareValueSortField(col, valuesCols.value?.length === 1)),
  )
})

const renderExportDimensionValue = (
  field: string,
  value: PivotTableData[string],
  isColumnDimension: boolean,
) => {
  const renderer = props.renderers?.[field]
  return renderer
    ? renderer(value, field, isColumnDimension ? 'header' : undefined)
    : value
}

const handleClickExport = () => {
  if (!props.data) return
  const rowFields = rowsList.value || []
  const colFields = colsList.value || []
  const dimensionFields = [...rowFields, ...colFields]
  const valueFields = valuesList.value || []
  const exportCols = [...dimensionFields, ...valueFields]
  const groups = new Map<string, { rules: PivotTableData, rows: PivotTableData[] }>()

  for (const item of props.data) {
    const rules: PivotTableData = {}
    let shouldSkipItem = false

    for (const field of dimensionFields) {
      let value = item[field]
      if (value === null) {
        shouldSkipItem = true
        break
      }
      if (value === undefined) {
        value = UNKNOWN_VALUE
      }
      if (Array.isArray(value)) {
        value = JSON.stringify(value)
      }
      rules[field] = value
    }

    if (shouldSkipItem) continue

    const key = JSON.stringify(rules)
    const group = groups.get(key)
    if (group) {
      group.rows.push(item)
    } else {
      groups.set(key, { rules, rows: [item] })
    }
  }

  const exportRows = Array.from(groups.values()).map(({ rules, rows }) => {
    const itemData: PivotTableData = Object.fromEntries(
      Object.entries(rules).map(([field, value]) => [
        field,
        renderExportDimensionValue(field, value, colFields.includes(field)),
      ]),
    )

    for (const field of valueFields) {
      const value = props.valueGetter?.(rows, field, { ...rules })
      itemData[field] = value !== undefined ? value : sumBy(rows, field)
    }

    return itemData
  })
  const headers = Object.fromEntries(exportCols.map(col => [col, props.captions?.[col] || col]))
  exportRows.unshift(headers)
  exportCSV('pivot', exportCols, exportRows, false)
}

const handleClickValue = (data: PivotTableClickValueData, amount: number) => {
  emit('click:value', data, amount)
}

const handleClickText = (data: string) => {
  emit('click:text', data)
}

const handleClickExpand = (data: boolean) => {
  contentRef.value?.expand(data)
}
</script>

<style>
.ui-pivot-table {
  @apply -mt-1 pt-1;
  @apply flex flex-auto flex-col;
  @apply gap-y-1 sm:gap-y-2;
  @apply sm:overflow-hidden;
  @apply select-none;

  &__content {
    @apply w-full;
    @apply flex flex-col;
  }

  &__container {
    @apply -mt-1 pt-1;
    @apply sm:flex sm:flex-auto;
    @apply gap-y-1 sm:gap-y-0 sm:gap-x-2;
    @apply sm:overflow-hidden;

    &--closed {
      @apply sm:gap-x-0.5;
    }

    &__no-data {
      @apply flex-auto;
    }
  }
}
</style>
